import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ysq_cart_v1";

const CartContext = createContext(null);

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

/**
 * Provider-agnostic cart. The frontend only stores display info + internal_id.
 * Pricing and provider details are resolved entirely on the backend at checkout.
 * Rules:
 *  - Only ONE subscription line is allowed per cart (adding another replaces it).
 *  - One-time fixed items merge by internal_id; custom donations stay separate.
 */
export function CartProvider({ children }) {
  const { user: authUser } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [emailCheckOpen, setEmailCheckOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const openCart = useCallback(() => setOpen(true), []);
  const closeCart = useCallback(() => {
    setOpen(false);
    setCheckoutUrl(null);
  }, []);

  const addItem = useCallback((item) => {
    // item: { internal_id, name, price, type: "subscription"|"one_time", custom_amount?, image? }
    setItems((prev) => {
      if (item.type === "subscription") {
        const existingSame = prev.find((i) => i.internal_id === item.internal_id);
        if (existingSame) {
          toast.info("That subscription is already in your cart.");
          return prev;
        }
        const hadOtherSub = prev.some((i) => i.type === "subscription");
        if (hadOtherSub) {
          toast.info("Only one subscription per checkout — updated your tier.");
        }
        const withoutSub = prev.filter((i) => i.type !== "subscription");
        return [...withoutSub, { ...item, lineId: item.internal_id, quantity: 1 }];
      }

      // Custom donation — always a unique line.
      if (item.internal_id === "donation_custom") {
        return [...prev, { ...item, lineId: `donation_custom_${Date.now()}`, quantity: 1 }];
      }

      // Mergeable one-time line.
      const existing = prev.find((i) => i.lineId === item.internal_id);
      if (existing) {
        return prev.map((i) =>
          i.lineId === item.internal_id
            ? { ...i, quantity: i.quantity + (item.quantity || 1) }
            : i
        );
      }
      return [...prev, { ...item, lineId: item.internal_id, quantity: item.quantity || 1 }];
    });
    setOpen(true);
    toast.success(`${item.name} added to cart`);
  }, []);

  const removeItem = useCallback((lineId) => {
    setCheckoutUrl(null);
    setItems((prev) => prev.filter((i) => i.lineId !== lineId));
  }, []);

  const updateQuantity = useCallback((lineId, quantity) => {
    setCheckoutUrl(null);
    setItems((prev) =>
      prev.map((i) =>
        i.lineId === lineId ? { ...i, quantity: Math.max(1, quantity) } : i
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCheckoutUrl(null);
    setItems([]);
  }, []);

  const count = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const subtotal = items.reduce(
    (sum, i) => sum + (Number(i.price) || 0) * (i.quantity || 1),
    0
  );
  const hasSubscription = items.some((i) => i.type === "subscription");

  const proceedToPayment = useCallback(async (emailOverride) => {
    if (items.length === 0) return;
    setCheckingOut(true);
    try {
      const payload = {
        items: items.map((i) => ({
          internal_id: i.internal_id,
          quantity: i.type === "subscription" ? 1 : i.quantity || 1,
          ...(i.internal_id === "donation_custom"
            ? { custom_amount: Number(i.custom_amount) }
            : {}),
        })),
        origin_url: window.location.origin,
        user_id: authUser?.id || null,
        user_email: emailOverride || authUser?.email || null,
      };

      const res = await axios.post(`${API}/payments/create-checkout`, payload);
      const url = res.data?.url;
      if (!url) throw new Error("No checkout URL received");

      // Always surface a fallback link (used if a popup blocker or the preview
      // iframe prevents the automatic redirect).
      setCheckoutUrl(url);
      setEmailCheckOpen(false);

      // Stripe Checkout blocks being framed, so we must open it at the TOP level.
      const inIframe = window.self !== window.top;
      if (inIframe) {
        try {
          window.top.location.href = url;
          return;
        } catch {
          const win = window.open(url, "_blank", "noopener");
          if (win) {
            setCheckingOut(false);
            return;
          }
          setCheckingOut(false);
          return;
        }
      }
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout could not be started. Please try again.");
      setCheckingOut(false);
    }
  }, [items, authUser]);

  const checkout = useCallback(async () => {
    if (items.length === 0) return;
    // Subscriptions must pass through the "Check Your Account" email screen first
    // (prevents duplicate monthly subscriptions). One-time carts go straight through.
    if (hasSubscription) {
      setOpen(false);
      setEmailCheckOpen(true);
      return;
    }
    await proceedToPayment();
  }, [items, hasSubscription, proceedToPayment]);

  const closeEmailCheck = useCallback(() => setEmailCheckOpen(false), []);

  const value = {
    items,
    open,
    openCart,
    closeCart,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    count,
    subtotal,
    hasSubscription,
    checkout,
    proceedToPayment,
    checkingOut,
    checkoutUrl,
    emailCheckOpen,
    closeEmailCheck,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
